import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'

import { getImpersonatedUser, useImpersonateUser } from '@/api/support'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { selectUserData } from '@/reducers/userDataReducer'
import { getApiErrorMessage } from '@/utils'
import { getRoleLandingPath, reloadAt, startImpersonation } from '@/utils/auth'

import * as S from './support-impersonation.styled'
import {
  canImpersonate,
  ImpersonationFormValues,
  makeImpersonationSchema,
} from './support-impersonation.utils'

type ApiError = { response?: { status?: number } }

export const SupportImpersonation = () => {
  const { t } = useTranslation(['user-panel'])
  const userData = useSelector(selectUserData)
  const { mutateAsync, isPending } = useImpersonateUser()
  // Covers verifying the new session after the mutation settles, up to the reload.
  const [isSwitching, setIsSwitching] = useState(false)

  const schema = useMemo(() => makeImpersonationSchema(t), [t])
  const form = useForm<ImpersonationFormValues>({
    defaultValues: { email: '' },
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  // Direct visits by anyone else (or while already impersonating) go back to the profile tab.
  if (!canImpersonate(userData)) return <Navigate replace to="/dashboard/user-panel/profile" />

  const showError = (message: string) => form.setError('email', { message })

  const onSubmit = async ({ email }: ImpersonationFormValues) => {
    let credentials
    try {
      credentials = await mutateAsync({ email })
    } catch (error) {
      const status = (error as ApiError)?.response?.status
      if (status === 404) showError(t('user-panel:impersonation.errors.userNotFound'))
      else if (status === 403) showError(t('user-panel:impersonation.errors.forbidden'))
      else showError(getApiErrorMessage(error))
      return
    }

    // Verify the new session before swapping it in, the same way a login does. Until it checks out
    // the cookies keep the support user's own session, so nothing else in the app is affected.
    setIsSwitching(true)
    let impersonatedUserData
    try {
      impersonatedUserData = await getImpersonatedUser(credentials)
    } catch {
      setIsSwitching(false)
      showError(t('user-panel:impersonation.errors.sessionRejected'))
      return
    }

    // Parking Staff can't use the web app (the login page rejects them too).
    if (impersonatedUserData.user.role === 'ParkingStaff') {
      setIsSwitching(false)
      showError(t('user-panel:impersonation.errors.parkingStaffNotAllowed'))
      return
    }

    startImpersonation(credentials)
    reloadAt(getRoleLandingPath(impersonatedUserData))
  }

  return (
    <S.Container>
      <Typography size="heading-tiny" weight="semibold">
        {t('user-panel:impersonation.title')}
      </Typography>
      <Typography size="text-medium" color="black700">
        {t('user-panel:impersonation.description')}
      </Typography>
      <FormProvider {...form}>
        <S.Form noValidate onSubmit={form.handleSubmit(onSubmit)}>
          <TextInputFormField
            required
            name="email"
            type="email"
            label={t('user-panel:impersonation.emailLabel')}
            placeholder={t('user-panel:impersonation.emailPlaceholder')}
          />
          <Button type="submit" loading={isPending || isSwitching}>
            {t('user-panel:impersonation.submit')}
          </Button>
        </S.Form>
      </FormProvider>
    </S.Container>
  )
}
